import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '@/utils/env';
import Navbar2 from '@/component/navbar2';
import Footer from '@/components/BrandFooter';
import L7Reveal from '@/components/landing/L7Reveal';
import { HumanBand } from '@/components/landing/L7Sections';
import { ArrowRight, CheckCircle2, MessageSquare, Sparkles } from 'lucide-react';
import '@/styles/landingL7.css';

/* Consulting overview pages — Financial & Strategic Advisory, AI & Technology.
   ---------------------------------------------------------------------------
   The "catalogue" counterparts to the service pages: hero + matched-brief card,
   snapshot strip, how-it-runs steps, the service catalogue, a difference table,
   the human band, proof, and a close.

   Two deliberate differences from the reference mockups:

   1. The brief-matching card is static. The mockup cycles three scenarios on a
      timer with staggered reveals; that is a scripted demo, not a live matcher,
      and shipping it as motion implies a system that is not wired yet. One
      honest frame, labelled as an example.
   2. The scripted strategist chat is a link to /contact. A fake conversation
      that books a slot into nothing is worse than a real form — wire it to the
      intake endpoint and it can come back.

   Proof cases carry outcomes without client names, as the mockups do. */

type Op = { ini: string; role: string; note: string; fit: string };
type Brief = { prompt: string; who: string; ops: Op[] };
type Sprint = { fam: string; title: string; body: string; meta: string; deliverables: string; popular?: boolean };
type Row = { axis: string; them: string; us: string };
type Case = { tag: string; title: string; body: string; result: string };

type Data = {
  eyebrow: string;
  h1: [string, string];
  lead: string;
  cta: string;
  briefs: Brief[];
  snap: { cat: string; value: string; unit?: string; note: string }[];
  steps: { n: string; title: string; body: string }[];
  catalogueTitle: string;
  catalogueLead: string;
  /** Route of the full practice-by-practice catalogue this page summarises. */
  catalogueHref: string;
  sprints: Sprint[];
  diffTitle: string;
  diffLead: string;
  rows: Row[];
  human: { title: string; lead: string; adviser: string; role: string; feats: string[]; chat: { from: 'them' | 'you'; text: string }[] };
  proofTitle: string;
  cases: Case[];
  close: { title: string; body: string };
};

const FINANCIAL: Data = {
  eyebrow: 'Conncct Consulting · Financial & Strategic Advisory',
  h1: ['Strategy and finance for the', 'decisions that matter.'],
  lead: 'Investor-grade strategy, modeling, valuation and story — combined into a clear, investable case. Fixed scope. A senior advisor is on your call within 24 hours.',
  cta: 'Describe your decision',
  briefs: [
    {
      prompt: "We're raising in 8 weeks — is our model and valuation investor-ready?",
      who: 'Founder · SaaS',
      ops: [
        { ini: 'CF', role: 'Corporate finance lead', note: 'Models, valuation, cap tables', fit: '96%' },
        { ini: 'FP', role: 'Ex-CFO advisor', note: 'Fundraising & investor decks', fit: '93%' },
        { ini: 'VC', role: 'Ex-VC / PE operator', note: 'Diligence & returns modeling', fit: '91%' },
      ],
    },
    {
      prompt: 'We have an acquisition offer on the table. Is the price fair?',
      who: 'Founder · consumer brand',
      ops: [
        { ini: 'VL', role: 'Valuation specialist', note: 'DCF, comps, precedent deals', fit: '97%' },
        { ini: 'MA', role: 'M&A advisor', note: 'Deal structure & negotiation', fit: '94%' },
        { ini: 'DD', role: 'Diligence lead', note: 'Revenue quality & risk', fit: '90%' },
      ],
    },
    {
      prompt: "We're entering Saudi next year — what does the business case look like?",
      who: 'COO · logistics',
      ops: [
        { ini: 'MS', role: 'Market strategy lead', note: 'GCC entry & feasibility', fit: '95%' },
        { ini: 'UE', role: 'Unit economics advisor', note: 'Pricing & margin modeling', fit: '92%' },
        { ini: 'RG', role: 'Regional operator', note: 'On-ground execution depth', fit: '89%' },
      ],
    },
  ],
  snap: [
    { cat: 'Speed', value: '24', unit: 'h', note: 'From your problem to a matched senior advisor' },
    { cat: 'Scope', value: 'Fixed', note: 'A defined deliverable — not an open-ended retainer' },
    { cat: 'Depth', value: 'Senior', note: 'CFO-level operators who have raised and sold' },
    { cat: 'Fee', value: 'Free', unit: ' to scope', note: 'You approve the scope before anything is charged' },
  ],
  steps: [
    { n: '01', title: 'Describe the decision', body: "One sentence on where you're stuck — a raise, a valuation, a deal, a pricing call. No brief to write." },
    { n: '02', title: 'Matched to a senior advisor', body: 'Within 24 hours, an operator who has solved exactly this is on your call.' },
    { n: '03', title: 'The work runs', body: 'Fixed-scope analysis against published deliverables — model, valuation, plan or deck.' },
    { n: '04', title: 'Decision-ready output', body: 'Hand-over with a working session that walks you through every number and slide.' },
  ],
  catalogueTitle: 'Pick the decision you need to make.',
  catalogueLead: 'Sold standalone or combined into a broader mandate — strategy, planning, modeling, valuation, pricing, transactions and storytelling.',
  catalogueHref: '/services/financial-advisory/catalogue',
  sprints: [
    { fam: 'Fundraising readiness', title: 'Startup fundraising readiness', body: 'Financial model, valuation, cap table, dilution scenarios, investor pitch deck and use of funds.', meta: 'Bundle', deliverables: '6 deliverables', popular: true },
    { fam: 'Modeling & valuation', title: 'Integrated financial model', body: 'Driver-based income statement, balance sheet and cash flow, with linked assumptions, runway and scenarios.', meta: 'Modeling', deliverables: 'Model + assumptions log' },
    { fam: 'Modeling & valuation', title: 'Company valuation', body: 'DCF, comparable companies, precedent transactions and venture methods — triangulated into a defensible range.', meta: 'Valuation', deliverables: 'Valuation memo + model', popular: true },
    { fam: 'Modeling & valuation', title: 'Cap table & dilution', body: 'Current and pro-forma ownership, round-by-round dilution, option-pool top-ups and founder-outcome ranges.', meta: 'Capital', deliverables: 'Pro-forma cap table' },
    { fam: 'Strategy & planning', title: 'Investor business plan', body: 'Investor-oriented case linking market opportunity, economics, growth, capital requirements and return potential.', meta: 'Planning', deliverables: 'Full business plan' },
    { fam: 'Strategy & planning', title: 'Market entry & feasibility', body: 'Market attractiveness, entry options, unit economics, risk assessment and sequencing for the UAE and GCC.', meta: 'Strategy', deliverables: 'Feasibility + roadmap' },
    { fam: 'Strategy & planning', title: 'Pricing & revenue model', body: 'Pricing architecture, tiers and packaging, monetization logic and price-volume-margin sensitivity.', meta: 'Pricing', deliverables: 'Pricing model + recs' },
    { fam: 'Transactions', title: 'Due diligence review', body: 'Commercial and financial diligence, revenue quality, margins and forecast consistency.', meta: 'Transactions', deliverables: 'DD report + risks' },
    { fam: 'Transactions', title: 'M&A / acquisition model', body: 'Purchase price, financing, pro-forma economics, returns and sensitivities, plus exit scenarios.', meta: 'Transactions', deliverables: 'Deal model + returns' },
    { fam: 'Storytelling', title: 'Investor pitch deck', body: 'The investment narrative, traction and economics, funding ask and use of funds — numbers drawn from your model.', meta: 'Storytelling', deliverables: 'Investor-ready deck' },
    { fam: 'Storytelling', title: 'Board / investor decision pack', body: 'Analysis, financial story, strategic options and a clear recommendation for boards and IC meetings.', meta: 'Storytelling', deliverables: 'Decision pack' },
    { fam: 'Strategy & planning', title: 'Strategic review & options', body: 'Build/buy/partner, funding alternatives, expansion or exit — with scenarios, economics and a recommendation.', meta: 'Strategy', deliverables: 'Options matrix + rec' },
  ],
  diffTitle: 'Top-tier depth, without the six-week engagement.',
  diffLead: '',
  rows: [
    { axis: 'Speed to scope', them: 'Weeks of proposals and scoping calls', us: 'A scoped plan within 24 hours' },
    { axis: 'Who you actually get', them: 'A pitch team, then juniors deliver', us: 'A senior operator, start to finish' },
    { axis: 'Commitment', them: 'Long retainers, open-ended hours', us: 'Fixed scope, fixed deliverable' },
    { axis: 'Fee model', them: 'Hourly meters or a % of your raise', us: 'Fixed fee — never a % of your raise' },
    { axis: 'Local depth', them: 'Generic, offshore analysis', us: 'Real UAE, Saudi & GCC execution depth' },
  ],
  human: {
    title: 'A senior advisor on your side — not a junior with a template.',
    lead: 'Every mandate is led by an operator who has actually raised, valued and sold businesses. Real judgment on the calls that matter, not a deck pulled from a database.',
    adviser: 'Karan', role: 'your advisor',
    feats: ['Named senior advisor', 'Investor-grade judgment', 'On your call in 24h', 'Fixed fee — never a % of your raise'],
    chat: [
      { from: 'them', text: 'Took a first look at your model — the CAC payback is the number an investor will push on. Want me to build the sensitivity before your meeting?' },
      { from: 'you', text: 'Yes please' },
      { from: 'them', text: 'Done — base, upside and downside are in, and the story now matches the numbers.' },
    ],
  },
  proofTitle: 'Start with the work that closed the deal.',
  cases: [
    { tag: 'Fundraising readiness', title: 'Seed round, SaaS', body: 'Model, valuation and investor deck rebuilt in a fixed scope ahead of two investor meetings.', result: 'Round closed' },
    { tag: 'Valuation', title: 'Consumer brand, GCC', body: 'Triangulated valuation range that held up against inbound acquisition interest.', result: 'Defended in the room' },
    { tag: 'Due diligence', title: 'Pre-investment review', body: 'Commercial and financial diligence surfaced the red flags before the term sheet was signed.', result: 'Priced with eyes open' },
  ],
  close: { title: 'Turn a complex decision into an investable case.', body: "Tell us the decision you're working toward. We'll suggest the right scope and put a senior advisor on your call within 24 hours." },
};

const AI: Data = {
  eyebrow: 'Conncct Consulting · AI & Technology',
  h1: ["AI that's actually", 'ready to ship.'],
  lead: 'We get your data and product AI-ready and turn prototypes into secure, production-grade systems. Fixed scope. A senior AI engineer is on your call within 24 hours.',
  cta: 'Describe your project',
  briefs: [
    {
      prompt: 'We built an MVP with AI tools — is it safe to put in front of real users?',
      who: 'Founder · SaaS',
      ops: [
        { ini: 'AI', role: 'Senior AI engineer', note: 'Agents · evaluation · governance', fit: '96%' },
        { ini: 'AR', role: 'Technical architect', note: 'Backend, data & API design', fit: '93%' },
        { ini: 'DE', role: 'Data engineer', note: 'Readiness & structuring', fit: '90%' },
      ],
    },
    {
      prompt: 'Our data is scattered across drives and sheets. Can we even use AI yet?',
      who: 'Ops lead · services firm',
      ops: [
        { ini: 'DE', role: 'Data engineer', note: 'Cleaning & knowledge bases', fit: '97%' },
        { ini: 'RD', role: 'AI readiness lead', note: 'Workflow & opportunity mapping', fit: '93%' },
        { ini: 'AR', role: 'Technical architect', note: 'Data layer design', fit: '88%' },
      ],
    },
    {
      prompt: 'We want an agent handling support — how do we do it without leaking client data?',
      who: 'CTO · fintech',
      ops: [
        { ini: 'GV', role: 'AI governance lead', note: 'Data boundaries & evaluation', fit: '96%' },
        { ini: 'AI', role: 'Senior AI engineer', note: 'Agent design & build', fit: '94%' },
        { ini: 'SC', role: 'Security reviewer', note: 'Privacy & deployment risk', fit: '91%' },
      ],
    },
  ],
  snap: [
    { cat: 'Speed', value: '24', unit: 'h', note: 'From your problem to a matched senior engineer' },
    { cat: 'Scope', value: 'Fixed', note: 'A defined deliverable — not an open-ended build' },
    { cat: 'Depth', value: 'Senior', note: 'AI engineers & technical architects, not juniors' },
    { cat: 'Fee', value: 'Free', unit: ' to scope', note: 'You approve the scope before anything is charged' },
  ],
  steps: [
    { n: '01', title: 'Assess', body: 'Understand the company, product, workflows and current data — where AI will actually create value.' },
    { n: '02', title: 'Diagnose', body: 'Identify the business, data, product and technical gaps, and match an operator who has shipped this before.' },
    { n: '03', title: 'Build or guide', body: 'Build the AI system, guide your team, or support implementation — with safety and evaluation baked in.' },
    { n: '04', title: 'Review', body: 'Test the output, review the risks, and prepare the system for real users — with a clear handover.' },
  ],
  catalogueTitle: 'Pick where your AI is stuck.',
  catalogueLead: 'Five practices, sold standalone or combined — readiness & data, agent build & integration, product advisory, code quality, and safety & governance.',
  catalogueHref: '/services/ai-technology/catalogue',
  sprints: [
    { fam: 'Readiness & data', title: 'AI readiness & data assessment', body: 'Workflow mapping, data-readiness review, AI opportunity map and an implementation roadmap.', meta: 'Bundle', deliverables: '4 deliverables', popular: true },
    { fam: 'Readiness & data', title: 'Data readiness & structuring', body: 'Clean and structure business knowledge; organise knowledge bases, databases or knowledge graphs.', meta: 'Data', deliverables: 'Structured data layer' },
    { fam: 'Readiness & data', title: 'AI opportunity map & roadmap', body: 'Identify where AI or automation helps, prioritise the highest-value use cases, and sequence the roadmap.', meta: 'Strategy', deliverables: 'Opportunity map + roadmap' },
    { fam: 'Build & integrate', title: 'AI agent design & build', body: 'Design the agent architecture, choose the stack, build the logic, and add safety and evaluation checks.', meta: 'Build', deliverables: 'Working AI agent', popular: true },
    { fam: 'Build & integrate', title: 'Product / backend integration', body: 'Connect the agent to your product or backend with your dev team — clean APIs and a maintainable handover.', meta: 'Integration', deliverables: 'API integration' },
    { fam: 'Quality & safety', title: 'AI MVP & codebase hardening', body: 'Turn a quick AI-built demo into a secure, tested product — separation, clean APIs, tests and deployment.', meta: 'Cleanup', deliverables: 'Production-ready code' },
    { fam: 'Quality & safety', title: 'Security & code review', body: 'Review the codebase for security, backend, data and quality risks before real users arrive.', meta: 'Review', deliverables: 'Risk & cleanup plan' },
    { fam: 'Advisory', title: 'AI product & architecture advisory', body: 'Decide what to build and what to avoid — prioritisation, architecture, developer guidance and risk review.', meta: 'Advisory', deliverables: 'Roadmap + reviews' },
    { fam: 'Build & integrate', title: 'Proof-of-concept / prototype', body: 'A focused first prototype or automation to prove value quickly, before committing to a full build.', meta: 'Prototype', deliverables: 'Working PoC' },
    { fam: 'Quality & safety', title: 'Safety, evaluation & governance', body: 'Clear data boundaries, privacy handling and an evaluation setup for AI outputs before real clients.', meta: 'Governance', deliverables: 'Evaluation setup' },
    { fam: 'Advisory', title: 'Deployment strategy', body: 'Cloud, open-source or private deployment — the right trade-off for your data privacy, cost and control.', meta: 'Advisory', deliverables: 'Deployment plan' },
    { fam: 'Quality & safety', title: 'Pre-launch technical review', body: 'Architecture, security, evaluation and quality review before go-live — a senior second pair of eyes.', meta: 'Review', deliverables: 'Go-live sign-off' },
  ],
  diffTitle: 'Senior AI engineering, safely — not a black box.',
  diffLead: '',
  rows: [
    { axis: 'Data & safety first', them: 'Straight to building agents', us: 'Readiness, data boundaries & evaluation first' },
    { axis: 'Who you actually get', them: 'Juniors on a learning curve', us: 'Senior AI engineers & technical architects' },
    { axis: 'Production quality', them: 'Working demo, weak backend', us: 'Secure, tested, maintainable, handed over' },
    { axis: 'Commitment', them: 'Open-ended hourly builds', us: 'Fixed scope, fixed deliverable' },
    { axis: 'Governance', them: 'No evaluation or data controls', us: 'Clear data boundaries & safe architecture' },
  ],
  human: {
    title: 'A senior engineer on your side — not a black box.',
    lead: 'Every build is led by a senior AI engineer or architect who ships production systems for a living. Real judgment on data boundaries, safety and architecture.',
    adviser: 'Khaled', role: 'your AI lead',
    feats: ['Named senior engineer', 'Safety & data boundaries first', 'On your call in 24h', 'Production-ready, handed over'],
    chat: [
      { from: 'them', text: 'Looked at your MVP — the backend and frontend are tangled and there are no tests. I would separate them and add an evaluation harness before you onboard anyone.' },
      { from: 'you', text: 'Makes sense' },
      { from: 'them', text: 'Done — clean APIs, tests passing, data boundaries set. Safe to put in front of real users now.' },
    ],
  },
  proofTitle: 'From quick prototype to production-grade.',
  cases: [
    { tag: 'MVP hardening', title: 'AI-built SaaS demo', body: 'Backend and frontend separated, clean APIs, tests and deployment added ahead of onboarding real clients.', result: 'Ready for real users' },
    { tag: 'Agent build', title: 'Support automation', body: 'An AI agent designed, built and integrated into the product backend, with evaluation and data governance.', result: 'Shipped safely' },
    { tag: 'Data readiness', title: 'Knowledge base', body: 'Scattered documents cleaned and structured into a usable data layer before any agent was built on top.', result: 'AI-ready data' },
  ],
  close: { title: 'From AI idea to a system you can ship.', body: "Tell us where your AI is stuck. We'll suggest the right scope and put a senior engineer on your call within 24 hours." },
};

/* Cycles the example briefs so the card shows the range of problems people
   arrive with, rather than one frozen scenario. The "Example" chip and the
   footnote stay put through every rotation — the motion is illustrative, and
   nothing here is scoring live operators.

   Holds still for anyone who asked their OS for reduced motion, and pauses on
   hover so a scenario can actually be read. */
function BriefCard({ briefs }: { briefs: Brief[] }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const still = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (still || paused || briefs.length < 2) return;
    const t = setInterval(() => setI(n => (n + 1) % briefs.length), 5200);
    return () => clearInterval(t);
  }, [still, paused, briefs.length]);

  const b = briefs[i];
  if (!b) return null;

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 22, boxShadow: 'var(--sh)', padding: '18px 18px 14px' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 12, borderBottom: '1px solid var(--line)' }}>
        <span style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--acc)', color: '#fff', display: 'grid', placeItems: 'center', flex: 'none' }}>
          <Sparkles size={17} strokeWidth={2.2} />
        </span>
        <b style={{ fontSize: 13.5 }}>Matching a brief</b>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 800, color: 'var(--acc-d)' }}>Example</span>
      </div>

      {/* Keyed on the index so React swaps the subtree and the fade replays. */}
      <div key={i} className="l7-briefswap">
        <div style={{ background: 'var(--acc)', color: '#fff', fontSize: 13.5, fontWeight: 600, padding: '11px 14px', borderRadius: 14, borderBottomRightRadius: 5, margin: '14px 0 5px', maxWidth: '88%', marginLeft: 'auto' }}>
          {b.prompt}
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700, textAlign: 'right', marginBottom: 13 }}>{b.who}</div>
        {b.ops.map((o, n) => (
          <div key={o.ini + o.role} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 11px', border: '1px solid var(--line)', borderRadius: 13, marginBottom: 9, animationDelay: `${0.12 + n * 0.11}s` }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, flex: 'none', background: 'var(--acc-soft)', color: 'var(--acc-d)', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 12 }}>{o.ini}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <b style={{ fontSize: 12.5, display: 'block' }}>{o.role}</b>
              <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>{o.note}</span>
            </span>
            <span style={{ textAlign: 'right', flex: 'none' }}>
              <b style={{ fontSize: 15, color: 'var(--green)', fontWeight: 800, display: 'block', lineHeight: 1 }}>{o.fit}</b>
              <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--faint)', fontWeight: 800 }}>fit</span>
            </span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
        <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700 }}>Illustrative — matched from senior operators, ranked by fit</span>
        {briefs.length > 1 && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 5, flex: 'none' }}>
            {briefs.map((_, n) => (
              <button
                key={n}
                onClick={() => setI(n)}
                aria-label={`Show example ${n + 1}`}
                style={{ width: n === i ? 16 : 6, height: 6, borderRadius: 999, border: 0, padding: 0, cursor: 'pointer', transition: 'width .25s, background .25s', background: n === i ? 'var(--acc)' : 'var(--line2, #D7DEE7)' }}
              />
            ))}
          </span>
        )}
      </div>
    </div>
  );
}

/* Conversational intake. This is a real form wearing a chat's clothes: it posts
   to the same /demo/submit endpoint as the contact page, so every submission
   lands in admin Leads. That endpoint requires all six of firstName, lastName,
   email, companyName, helpWith and discussionTopic, which is what the five
   questions below collect.

   The last question asks when someone prefers to be called and sends it as part
   of the topic. It deliberately does not present bookable slots — there is no
   calendar behind this, and offering times we cannot honour is worse than
   asking. */
const ASK = [
  { key: 'topic', q: 'What are you working toward? One sentence is plenty.', ph: 'e.g. raising a seed round in Q4' },
  { key: 'company', q: 'Got it. What is the company called?', ph: 'Company name' },
  { key: 'name', q: 'And who am I speaking with?', ph: 'Your full name' },
  { key: 'email', q: 'Best email for the advisor to reach you on?', ph: 'you@company.com' },
] as const;

const WHEN = ['Mornings', 'Afternoons', 'Either works'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function IntakeChat({ d }: { d: Data }) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus follows the conversation, so answering never needs the mouse.
  useEffect(() => { if (step < ASK.length) inputRef.current?.focus(); }, [step]);

  const practice = d.eyebrow.split('·').pop()?.trim() ?? 'Consulting';
  const total = ASK.length + 1;
  const pct = Math.round((Math.min(step, total) / total) * 100);
  const current = ASK[step];
  const invalid = current?.key === 'email' && draft.trim() !== '' && !EMAIL_RE.test(draft.trim());

  const answer = (value: string) => {
    const v = value.trim();
    if (!v || !current) return;
    if (current.key === 'email' && !EMAIL_RE.test(v)) return;
    setAnswers(a => ({ ...a, [current.key]: v }));
    setDraft('');
    setStep(s => s + 1);
  };

  const send = async (when: string) => {
    // Annotated because spreading a Record into an object literal drops the
    // index signature, leaving TS with just `{ when: string }`.
    const full: Record<string, string> = { ...answers, when };
    setAnswers(full);
    setStep(total);
    setStatus('sending');
    const name = (full.name ?? '').trim();
    try {
      const res = await fetch(`${API_BASE_URL}/demo/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: name.split(' ')[0] || name,
          // The endpoint rejects an empty lastName, and a one-word name is a
          // perfectly normal thing to type.
          lastName: name.split(' ').slice(1).join(' ') || '—',
          email: full.email ?? '',
          companyName: full.company ?? 'Not given',
          helpWith: practice,
          discussionTopic: `${full.topic ?? ''} · Prefers ${when.toLowerCase()}`,
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  const said = ASK.filter(a => answers[a.key]);

  return (
    <div style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 22, boxShadow: 'var(--sh)', overflow: 'hidden', textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '15px 18px', borderBottom: '1px solid var(--line)' }}>
        <span style={{ width: 36, height: 36, borderRadius: 11, background: 'var(--acc)', color: '#fff', display: 'grid', placeItems: 'center', flex: 'none' }}>
          <MessageSquare size={17} strokeWidth={2.2} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <b style={{ fontSize: 13.5, display: 'block', color: 'var(--ink)' }}>Scope your mandate</b>
          <span style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 700 }}>{practice} · takes about a minute</span>
        </span>
      </div>

      <div style={{ height: 3, background: 'var(--line)' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--acc)', transition: 'width .35s ease' }} />
      </div>

      <div style={{ padding: '18px' }}>
        {said.map(a => (
          <div key={a.key}>
            <div style={{ fontSize: 13, color: 'var(--ink-2, var(--muted))', fontWeight: 600, margin: '0 0 8px', maxWidth: '88%' }}>{a.q}</div>
            <div style={{ background: 'var(--acc)', color: '#fff', fontSize: 13.5, fontWeight: 600, padding: '10px 13px', borderRadius: 14, borderBottomRightRadius: 5, marginBottom: 16, maxWidth: '88%', marginLeft: 'auto' }}>
              {answers[a.key]}
            </div>
          </div>
        ))}

        {current && (
          <>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)', marginBottom: 12 }}>{current.q}</div>
            {step === 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 12 }}>
                {d.briefs.map(b => (
                  <button key={b.prompt} onClick={() => answer(b.prompt)}
                    style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--acc-d)', background: 'var(--acc-soft)', border: '1px solid var(--line)', borderRadius: 999, padding: '6px 11px', cursor: 'pointer', textAlign: 'left' }}>
                    {b.prompt.length > 54 ? `${b.prompt.slice(0, 52)}…` : b.prompt}
                  </button>
                ))}
              </div>
            )}
            <form onSubmit={e => { e.preventDefault(); answer(draft); }} style={{ display: 'flex', gap: 8 }}>
              <input
                ref={inputRef}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder={current.ph}
                type={current.key === 'email' ? 'email' : 'text'}
                aria-label={current.q}
                aria-invalid={invalid || undefined}
                style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, padding: '11px 13px', borderRadius: 12, border: `1px solid ${invalid ? '#DC2626' : 'var(--line2, var(--line))'}`, outline: 'none', color: 'var(--ink)', background: '#fff' }}
              />
              <button type="submit" disabled={!draft.trim() || invalid} aria-label="Send answer"
                style={{ flex: 'none', width: 44, borderRadius: 12, border: 0, background: 'var(--acc)', color: '#fff', display: 'grid', placeItems: 'center', cursor: draft.trim() && !invalid ? 'pointer' : 'not-allowed', opacity: draft.trim() && !invalid ? 1 : .45 }}>
                <ArrowRight size={17} />
              </button>
            </form>
            {invalid && <div style={{ fontSize: 11.5, color: '#DC2626', fontWeight: 700, marginTop: 7 }}>That does not look like an email address yet.</div>}
          </>
        )}

        {step === ASK.length && (
          <>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)', marginBottom: 12 }}>Last one — when suits you for the call?</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {WHEN.map(w => (
                <button key={w} onClick={() => send(w)}
                  style={{ fontSize: 13, fontWeight: 800, color: '#fff', background: 'var(--acc)', border: 0, borderRadius: 12, padding: '10px 16px', cursor: 'pointer' }}>
                  {w}
                </button>
              ))}
            </div>
          </>
        )}

        {status === 'sending' && <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)' }}>Sending…</div>}

        {status === 'sent' && (
          <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
            <span style={{ width: 34, height: 34, borderRadius: 10, flex: 'none', background: 'var(--green-soft)', color: 'var(--green)', display: 'grid', placeItems: 'center' }}>
              <CheckCircle2 size={18} strokeWidth={2.2} />
            </span>
            <span>
              <b style={{ fontSize: 14, display: 'block', color: 'var(--ink)' }}>That is with us.</b>
              <span style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>
                A senior advisor will come back within 24 hours with times that fit {(answers.when ?? '').toLowerCase()}, and a suggested scope. Nothing is charged until you approve it.
              </span>
            </span>
          </div>
        )}

        {status === 'error' && (
          <div style={{ fontSize: 13, fontWeight: 700, color: '#DC2626' }}>
            That did not send. Please try{' '}
            <Link to="/contact" style={{ color: 'var(--acc-d)', textDecoration: 'underline' }}>the contact form</Link>{' '}
            instead — we do not want to lose your details.
          </div>
        )}
      </div>
    </div>
  );
}

function Overview({ d }: { d: Data }) {
  return (
    <div className="l7">
      <L7Reveal />
      <Navbar2 />

      {/* ── Hero + matched-brief card ────────────────────────── */}
      <section className="l7-sec" style={{ background: 'radial-gradient(1000px 560px at 78% -12%, var(--acc-soft) 0%, transparent 60%), var(--cream)' }}>
        <div className="l7-wrap l7-split">
          <div>
            <span className="l7-eyebrow">{d.eyebrow}</span>
            <h1 style={{ margin: '18px 0 0' }}>{d.h1[0]}<br /><span className="l7-serif">{d.h1[1]}</span></h1>
            <p className="l7-lead" style={{ margin: '20px 0 28px' }}>{d.lead}</p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <a href="#scope" className="l7-btn l7-btn--acc">{d.cta} <ArrowRight size={16} /></a>
              <a href="#how" className="l7-btn l7-btn--ghost">How it works</a>
            </div>
          </div>

          <BriefCard briefs={d.briefs} />
        </div>

        <div className="l7-wrap" style={{ marginTop: 48 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', border: '1px solid var(--line)', borderRadius: 18, background: '#fff', overflow: 'hidden', boxShadow: 'var(--sh-sm)' }}>
            {d.snap.map(s => (
              <div key={s.cat} style={{ padding: '18px 20px', borderRight: '1px solid var(--line)' }}>
                <span style={{ display: 'block', fontSize: 10, fontWeight: 800, letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--acc-d)', marginBottom: 8 }}>{s.cat}</span>
                <b style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-.04em', display: 'block', lineHeight: 1 }}>
                  {s.value}{s.unit && <span style={{ fontSize: 14, color: 'var(--muted)', fontWeight: 800 }}>{s.unit}</span>}
                </b>
                <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700, display: 'block', marginTop: 6 }}>{s.note}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it runs ──────────────────────────────────────── */}
      <section className="l7-sec l7-sec--paper" id="how" style={{ borderTop: '1px solid var(--line)' }}>
        <div className="l7-wrap">
          <div style={{ maxWidth: 720, marginBottom: 40 }}>
            <span className="l7-eyebrow">The shape</span>
            <h2 style={{ margin: '12px 0 10px' }}>Sell decisions and outcomes — <span className="l7-serif">not hours.</span></h2>
            <p className="l7-lead">Every engagement follows the same rhythm — scope, match, build, decide — and ends in a decision-ready deliverable.</p>
          </div>
          <div className="l7-grid l7-grid--4">
            {d.steps.map(s => (
              <div className="l7-card" key={s.n}>
                <span className="l7-serif" style={{ fontSize: 14, color: 'var(--acc-d)', display: 'block', marginBottom: 10 }}>{s.n}</span>
                <h3 style={{ fontSize: 16, marginBottom: 7 }}>{s.title}</h3>
                <p style={{ fontSize: 13.5 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Catalogue ────────────────────────────────────────── */}
      <section className="l7-sec" style={{ borderTop: '1px solid var(--line)' }}>
        <div className="l7-wrap">
          <div style={{ maxWidth: 720, marginBottom: 30 }}>
            <span className="l7-eyebrow">The service catalogue</span>
            <h2 style={{ margin: '12px 0 10px' }}>{d.catalogueTitle}</h2>
            <p className="l7-lead">{d.catalogueLead}</p>
          </div>
          <div className="l7-grid l7-grid--3">
            {d.sprints.map(sp => (
              <div className="l7-card" key={sp.title} style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
                {sp.popular && (
                  <span style={{ position: 'absolute', top: 0, right: 20, transform: 'translateY(-50%)', fontSize: 9.5, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: '#fff', background: 'var(--acc)', borderRadius: 999, padding: '5px 11px' }}>Most popular</span>
                )}
                <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--acc-d)', background: 'var(--acc-soft)', borderRadius: 999, padding: '4px 10px', alignSelf: 'flex-start', marginBottom: 14 }}>{sp.fam}</span>
                <h3 style={{ fontSize: 19, marginBottom: 8 }}>{sp.title}</h3>
                <p style={{ fontSize: 13.5, flex: 1 }}>{sp.body}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 18, paddingTop: 15, borderTop: '1px solid var(--line)', fontSize: 12, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>
                  <span>{sp.meta}</span><span>{sp.deliverables}</span>
                </div>
                <div style={{ marginTop: 11, fontSize: 12.5, fontWeight: 800, color: 'var(--acc-d)' }}>Fixed scope · scoped on your call</div>
              </div>
            ))}
          </div>
          <Link to={d.catalogueHref} className="l7-btn l7-btn--acc" style={{ marginTop: 26 }}>
            See every service in detail <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ── The difference ───────────────────────────────────── */}
      <section className="l7-sec l7-sec--paper" style={{ borderTop: '1px solid var(--line)' }}>
        <div className="l7-wrap">
          <div style={{ maxWidth: 720, marginBottom: 34 }}>
            <span className="l7-eyebrow">The difference</span>
            <h2 style={{ margin: '12px 0 10px' }}>{d.diffTitle}</h2>
          </div>
          <div style={{ border: '1px solid var(--line)', borderRadius: 18, overflow: 'hidden', background: '#fff' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1fr) minmax(0,1fr)', background: 'var(--paper2)', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--muted)' }}>
              <div style={{ padding: '16px 18px' }}>&nbsp;</div>
              <div style={{ padding: '16px 18px' }}>Traditional firm</div>
              <div style={{ padding: '16px 18px' }}>A Conncct engagement</div>
            </div>
            {d.rows.map(r => (
              <div key={r.axis} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1fr) minmax(0,1fr)', borderTop: '1px solid var(--line)', fontSize: 13.5 }}>
                <div style={{ padding: '16px 18px', fontWeight: 800 }}>{r.axis}</div>
                <div style={{ padding: '16px 18px', color: 'var(--muted)' }}>{r.them}</div>
                <div style={{ padding: '16px 18px', fontWeight: 700, background: 'var(--acc-soft)' }}>{r.us}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HumanBand
        eyebrow="The human touch"
        title={d.human.title}
        lead={d.human.lead}
        adviser={d.human.adviser}
        role={d.human.role}
        chat={d.human.chat}
        feats={d.human.feats}
      />

      {/* ── Proof ────────────────────────────────────────────── */}
      <section className="l7-sec l7-sec--navy">
        <div className="l7-wrap">
          <span className="l7-eyebrow">The proof</span>
          <h2 style={{ color: '#fff', margin: '14px 0 10px' }}>{d.proofTitle}</h2>
          <p className="l7-lead" style={{ maxWidth: 560 }}>Real shapes of problem, the work that ran, and what moved. Names withheld; outcomes are the point.</p>
          <div className="l7-grid l7-grid--3" style={{ marginTop: 38 }}>
            {d.cases.map(c => (
              <div key={c.title} style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 18, padding: 24 }}>
                <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: '#5B9BD5' }}>{c.tag}</span>
                <h3 style={{ color: '#fff', fontSize: 18, margin: '10px 0 8px' }}>{c.title}</h3>
                <p style={{ fontSize: 13, color: '#C2CCDA' }}>{c.body}</p>
                <div className="l7-serif" style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,.12)', fontSize: 22, color: '#fff' }}>{c.result}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Close ────────────────────────────────────────────── */}
      <section className="l7-sec" id="scope">
        <div className="l7-wrap l7-split">
          <div>
            <span className="l7-eyebrow">Start here</span>
            <h2 style={{ margin: '14px 0 10px' }}>{d.close.title}</h2>
            <p className="l7-lead">{d.close.body}</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '22px 0 0', display: 'grid', gap: 10 }}>
              {['Free to scope — you approve before anything is charged', 'A senior advisor, not a sales team', 'Fixed scope and a defined deliverable'].map(t => (
                <li key={t} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13.5, fontWeight: 700, color: 'var(--ink-2, var(--muted))' }}>
                  <CheckCircle2 size={17} strokeWidth={2.3} style={{ color: 'var(--green)', flex: 'none', marginTop: 1 }} />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <IntakeChat d={d} />
        </div>
      </section>

      <Footer />
    </div>
  );
}

export function AiOverview() { return <Overview d={AI} />; }
export default function FinancialOverview() { return <Overview d={FINANCIAL} />; }
